import firebase from "firebase/app";
import { escapeKey } from "../../lib/fire-utils";
import { isPlanningType, isProblemType } from "../document/document-types";
// Each tab's least time between the acknowledgment of one write and the start of the next.
export const kActivityWriteIntervalMs = 5000;
// How long a change whose history has been uploaded waits for a content save that covers it.
export const kActivitySaveTimeoutMs = 30000;
// Each consecutive failed write doubles the wait before the next, up to this.
export const kActivityMaxRetryDelayMs = 60000;
// Entries a running Simulator or DataFlow tile records on its own, every step. They are not activity.
const kTickEntries = [
    { model: "SimulatorTool", actionSuffix: "/step" },
    { model: "DataflowProgram", actionSuffix: "/tickAndProcess" }
];
export function isTickEntry(entry) {
    return kTickEntries.some(({ model, actionSuffix }) => { var _a; return entry.model === model && !!((_a = entry.action) === null || _a === void 0 ? void 0 : _a.endsWith(actionSuffix)); });
}
/**
 * Keeps the launched student's researcher dashboard activity document,
 * researcher_dashboard/{portal}/activity/{classHash}/users/{uid}, current. A change counts once its
 * history entry has been uploaded and a content save covering it has succeeded; writes are at least
 * kActivityWriteIntervalMs apart and each covers every change that has landed by then.
 */
export class ActivityRecorder {
    constructor(stores) {
        Object.defineProperty(this, "stores", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: stores
        });
        Object.defineProperty(this, "pending", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        // The changes of failed writes, which have all landed, kept as whether any was in the launch offering.
        Object.defineProperty(this, "failed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "savedIds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new WeakMap()
        });
        Object.defineProperty(this, "inFlight", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "lastAckAt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "failures", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "listening", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "timer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "timerDueAt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "handlePageHide", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (!this.isEnabled || !this.hasWork)
                    return;
                this.clearTimer();
                this.write([...this.pending]);
            }
        });
    }
    dispose() {
        window.removeEventListener("pagehide", this.handlePageHide);
        this.listening = false;
        this.clearTimer();
    }
    get isEnabled() {
        const { appMode, user } = this.stores;
        return appMode === "authed" && user.isStudent && !!user.classHash && !!user.offeringId;
    }
    get documentPath() {
        const { user } = this.stores;
        return `researcher_dashboard/${escapeKey(user.portal)}/activity/${user.classHash}/users/${user.id}`;
    }
    isInLaunchOffering(document) {
        const { user } = this.stores;
        if (isProblemType(document.type) || isPlanningType(document.type))
            return true;
        return !!user.userIdForGroupDocuments && document.uid === user.userIdForGroupDocuments;
    }
    onLocalEntryUploaded(document, entry) {
        if (!this.isEnabled || isTickEntry(entry))
            return;
        if (!this.listening) {
            window.addEventListener("pagehide", this.handlePageHide);
            this.listening = true;
        }
        this.pending.push({
            entryId: entry.id, document, inLaunchOffering: this.isInLaunchOffering(document), uploadedAt: Date.now()
        });
        this.schedule();
    }
    /**
     * Called when this client's content save of the document succeeds, with the history entry id stamped on it.
     * The id is kept because an entry's upload can land after the save that covers it.
     */
    onContentSaved(document, lastHistoryEntryId) {
        this.savedIds.set(document, lastHistoryEntryId);
        this.schedule();
    }
    isSaved(change) {
        var _a;
        const { document, entryId } = change;
        const savedId = this.savedIds.get(document);
        if (!savedId)
            return false;
        const history = (_a = document.treeManagerAPI) === null || _a === void 0 ? void 0 : _a.document.history;
        if (!history)
            return false;
        const entryIndex = history.findIndex(e => e.id === entryId);
        const savedIndex = history.findIndex(e => e.id === savedId);
        return entryIndex >= 0 && savedIndex >= entryIndex;
    }
    hasLanded(change, now) {
        return this.isSaved(change) || now - change.uploadedAt >= kActivitySaveTimeoutMs;
    }
    setTimer(dueAt) {
        if (this.timer && this.timerDueAt !== undefined && this.timerDueAt <= dueAt)
            return;
        this.clearTimer();
        this.timerDueAt = dueAt;
        this.timer = setTimeout(() => {
            this.timer = undefined;
            this.timerDueAt = undefined;
            this.schedule();
        }, Math.max(0, dueAt - Date.now()));
    }
    clearTimer() {
        if (this.timer)
            clearTimeout(this.timer);
        this.timer = undefined;
        this.timerDueAt = undefined;
    }
    get hasWork() {
        return this.pending.length > 0 || !!this.failed;
    }
    schedule() {
        if (this.inFlight || !this.hasWork)
            return;
        const now = Date.now();
        const landed = this.pending.filter(change => this.hasLanded(change, now));
        if (landed.length === 0 && !this.failed) {
            this.setTimer(Math.min(...this.pending.map(change => change.uploadedAt + kActivitySaveTimeoutMs)));
            return;
        }
        const wait = Math.min(kActivityWriteIntervalMs * Math.pow(2, this.failures), kActivityMaxRetryDelayMs);
        const dueAt = this.lastAckAt === undefined ? now : this.lastAckAt + wait;
        if (now < dueAt) {
            this.setTimer(dueAt);
            return;
        }
        this.write(landed);
    }
    write(changes) {
        var _a;
        const { user, db } = this.stores;
        const { FieldValue } = firebase.firestore;
        this.pending = this.pending.filter(change => !changes.includes(change));
        const inLaunchOffering = !!((_a = this.failed) === null || _a === void 0 ? void 0 : _a.inLaunchOffering) || changes.some(change => change.inLaunchOffering);
        this.failed = undefined;
        const data = {
            lastActivityAt: FieldValue.serverTimestamp(),
            writeCount: FieldValue.increment(1)
        };
        if (inLaunchOffering) {
            data.changed = { [user.offeringId]: FieldValue.serverTimestamp() };
        }
        this.inFlight = true;
        db.firestore.documentRef(this.documentPath).set(data, { merge: true })
            .then(() => {
            this.failures = 0;
        })
            .catch(error => {
            var _a;
            this.failed = { inLaunchOffering: inLaunchOffering || !!((_a = this.failed) === null || _a === void 0 ? void 0 : _a.inLaunchOffering) };
            this.failures++;
            console.warn("Could not record researcher dashboard activity", error);
        })
            .finally(() => {
            this.lastAckAt = Date.now();
            this.inFlight = false;
            this.schedule();
        });
    }
}
//# sourceMappingURL=data:application/json;base64,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